import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';

// Status config (matches blueprint sBadge)
const STATUS_STYLE = {
  Todo:         { color:'#6b7280',  cls:'badge-gray',   icon:'📋' },
  'In Progress':{ color:'#3b7ef0',  cls:'badge-blue',   icon:'⚡' },
  Review:       { color:'#f7c34f',  cls:'badge-yellow', icon:'👁' },
  Done:         { color:'#1db87a',  cls:'badge-green',  icon:'✅' },
  Blocked:      { color:'#e84545',  cls:'badge-red',    icon:'⛔' },
  Locked:       { color:'#374151',  cls:'badge-gray',   icon:'🔒' },
};

const PRIORITY_LABEL = { urgent:'🔴 Urgent', high:'🟠 High', normal:'🟡 Normal', low:'🟢 Low' };

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fltStyle = {padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',cursor:'pointer',width:'auto'};

// Matches blueprint WORK_PAGE_TABS exactly
const WORK_PAGE_TABS = {
  creatives:       [{id:'pending',label:'In Progress'},{id:'internal',label:'Internal Review'},{id:'client',label:'Client Review'},{id:'approved',label:'Approved'}],
  postcopy:        [{id:'draft',label:'Draft'},{id:'internal',label:'Internal Approval'},{id:'client',label:'Client Approval'},{id:'approved',label:'Approved'}],
  contentcalendar: [{id:'scripts',label:'Scripts & Briefs'},{id:'internal',label:'Internal Approval'},{id:'client',label:'Client Approval'},{id:'approved',label:'Approved'}],
  scheduling:      [{id:'ready',label:'Ready to Schedule'},{id:'scheduled',label:'Scheduled'},{id:'posted',label:'Posted / Live'}],
  monitoring:      [{id:'active',label:'Active Monitoring'},{id:'closed',label:'Closed'}],
};

const STATUS_TO_TAB = {
  creatives:       {Draft:'pending',Rejected:'pending','Pending Internal':'internal','Pending Client':'client',Approved:'approved'},
  postcopy:        {Draft:'draft',Rejected:'draft','Rejected Internal':'draft','Rejected Client':'draft','Pending Internal':'internal','Sent to Client':'client','Pending Client':'client',Approved:'approved','Approved Directly':'approved'},
  contentcalendar: {Draft:'scripts',Rejected:'scripts','Pending Internal':'internal','Pending Client':'client',Approved:'approved'},
};

const TAB_BADGE = { pending:'badge-blue',draft:'badge-blue',scripts:'badge-blue',internal:'badge-yellow',client:'badge-purple',approved:'badge-green',ready:'badge-blue',scheduled:'badge-yellow',posted:'badge-green',active:'badge-blue',closed:'badge-green' };
const STATUS_BADGE = { Todo:'badge-gray','In Progress':'badge-blue',Review:'badge-yellow',Done:'badge-green',Blocked:'badge-red',Locked:'badge-gray' };

// Blueprint: taskTabLabel — returns { label, tabId } or null
function getTabLabel(task) {
  const page = (task.linked_page_id || '').split('.')[0];
  if (!STATUS_TO_TAB[page]) return null;
  const st    = task.module_status || 'Draft';
  const tabId = STATUS_TO_TAB[page][st] || Object.keys(STATUS_TO_TAB[page])[0];
  const tab   = (WORK_PAGE_TABS[page] || []).find(x => x.id === tabId);
  return tab ? { label: tab.label, tabId } : null;
}

// Blueprint: taskTabProgress — 25/50/75/100 based on approval stage
function getTabProgress(task) {
  const page = (task.linked_page_id || '').split('.')[0];
  const POS = {
    creatives:       (st) => ({Draft:1,Rejected:1,'Pending Internal':2,'Pending Client':3,Approved:4}[st] || 1),
    postcopy:        (st) => ({Draft:1,Rejected:1,'Rejected Internal':1,'Rejected Client':1,'Pending Internal':2,'Sent to Client':3,'Pending Client':3,Approved:4,'Approved Directly':4}[st] || 1),
    contentcalendar: (st) => ({Draft:1,Rejected:1,'Pending Internal':2,'Pending Client':3,Approved:4}[st] || 1),
  };
  if (POS[page]) {
    return POS[page](task.module_status || 'Draft') * 25; // 25, 50, 75, 100
  }
  // Scheduling / Monitoring / Planning — use task status
  if (task.status === 'Done') return 100;
  if (task.status === 'In Progress') return 50;
  return task.progress || 0;
}

function fmtDMY(iso) {
  if (!iso) return '—';
  const [y,m,d] = (iso||'').split('-');
  return d ? `${d}-${m}-${y}` : iso;
}

function memberInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
}

function memberColor(name) {
  const colors = ['#6c58f0','#3b7ef0','#e87830','#1db87a','#e84545','#f7c34f','#0a66c2'];
  let h = 0;
  for (const c of (name||'')) h = (h*31 + c.charCodeAt(0)) & 0xffffff;
  return colors[Math.abs(h) % colors.length];
}

// ── Sub-tab progress strip ────────────────────────────────────────────────────
function SubTabProgress({ task, phaseColor }) {
  // Page sub-tabs per linked page (matches blueprint WORK_PAGE_TABS)
  const PAGE_TABS = {
    contentcalendar: [{id:'scripts',label:'Script & Brief'},{id:'internal',label:'Internal'},{id:'client',label:'Client'},{id:'approved',label:'Approved'}],
    creatives:       [{id:'pending',label:'In Progress'},{id:'internal',label:'Internal'},{id:'client',label:'Client'},{id:'approved',label:'Approved'}],
    postcopy:        [{id:'draft',label:'Draft'},{id:'internal',label:'Internal'},{id:'client',label:'Client'},{id:'approved',label:'Approved'}],
    scheduling:      [{id:'ready',label:'Ready'},{id:'scheduled',label:'Scheduled'},{id:'posted',label:'Posted'}],
    monitoring:      [{id:'active',label:'Active'},{id:'closed',label:'Closed'}],
  };

  const linkedPageId = task.linked_page_id || '';
  const [page]       = linkedPageId.split('.');
  const tabs         = PAGE_TABS[page];
  if (!tabs || tabs.length <= 1) return null;

  // Current tab index from module_status field
  const moduleStatus = task.module_status || '';
  let curIdx = 0;
  if (page === 'creatives') {
    curIdx = {Draft:0,Rejected:0,'Pending Internal':1,'Pending Client':2,Approved:3}[moduleStatus] ?? 0;
  } else if (page === 'postcopy') {
    curIdx = {Draft:0,Rejected:0,'Pending Internal':1,'Pending Client':2,Approved:3,'Approved Directly':3}[moduleStatus] ?? 0;
  } else if (page === 'contentcalendar') {
    curIdx = {Draft:0,Rejected:0,'Pending Internal':1,'Pending Client':2,Approved:3}[moduleStatus] ?? 0;
  } else if (page === 'scheduling') {
    if (moduleStatus === 'posted') curIdx = 2;
    else if (moduleStatus === 'scheduled') curIdx = 1;
    else curIdx = 0;
  } else if (page === 'monitoring') {
    curIdx = moduleStatus === 'Closed' ? 1 : 0;
  }

  return (
    <div style={{margin:'8px 0 0',padding:'10px 12px',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:8,borderTop:`3px solid ${phaseColor}`}}>
      <div style={{fontSize:'0.68rem',fontWeight:700,color:'var(--text3)',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.5px'}}>
        📊 Sub-tab Progress — {task.phase}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:0,flexWrap:'nowrap',overflowX:'auto'}}>
        {tabs.map((tab, i) => {
          const isDone    = i < curIdx;
          const isCurrent = i === curIdx;
          const dotColor  = isDone ? '#1db87a' : isCurrent ? phaseColor : 'var(--border2)';
          const connector = i < tabs.length-1
            ? <div style={{flex:1,minWidth:20,height:2,background:isDone?'#1db87a':'var(--border)',marginTop:-8}} />
            : null;
          return (
            <React.Fragment key={tab.id}>
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,minWidth:70}}>
                <div style={{width:28,height:28,borderRadius:'50%',background:isCurrent?phaseColor:isDone?'#1db87a':'var(--bg3)',border:`2px solid ${dotColor}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.68rem',fontWeight:800,color:isDone||isCurrent?'#fff':'var(--text3)'}}>
                  {isDone ? '✓' : isCurrent ? '●' : '○'}
                </div>
                <div style={{fontSize:'0.62rem',fontWeight:isCurrent?700:500,color:isDone?'#1db87a':isCurrent?phaseColor:'var(--text3)',textAlign:'center',lineHeight:1.2}}>
                  {tab.label}
                </div>
                {isCurrent && <div style={{fontSize:'0.55rem',fontWeight:700,color:phaseColor,whiteSpace:'nowrap'}}>← current</div>}
              </div>
              {connector}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Task row with expandable sub-tab detail ───────────────────────────────────
function TaskRow({ task, onEdit, onHold, onResume }) {
  const [expanded, setExpanded] = useState(false);

  const st        = STATUS_STYLE[task.status] || STATUS_STYLE['Todo'];
  const phColor   = task.phase_color || '#9ba3b5';
  const avatarBg  = memberColor(task.assignee);
  const progress  = task.progress || 0;
  const progColor = progress>=100?'#1db87a':progress>=50?'#3b7ef0':'#f7c34f';
  const hasSubTabs= !!(task.linked_page_id && task.linked_page_id.includes('.'));

  const [ey,em,ed] = (task.event_date||'').split('-');
  const eventDisplay = ed ? `${ed}-${em}-${ey}` : '—';
  const isOverdue = task.event_date && task.status!=='Done' && new Date(task.event_date)<new Date();

  // Stage window — start = prev task deadline, target = this task deadline
  const winState = task.stage_state || 'upcoming';
  const winColor = winState==='overdue'?'var(--red)':winState==='current'?'var(--accent)':winState==='done'?'var(--green)':'var(--text3)';
  const winBadge = winState==='overdue'?'⚠️ overdue':winState==='current'?'● active now':winState==='upcoming'?'◌ upcoming':'✓ done';

  // Days to start
  const daysToStart = (() => {
    if (!task.stage_start || task.status==='Done') return null;
    const sD = new Date(task.stage_start+'T00:00:00');
    const today = new Date(); today.setHours(0,0,0,0);
    return Math.round((sD-today)/86400000);
  })();

  const daysChip = (() => {
    if (daysToStart === null) return <span style={{color:'var(--text3)',fontSize:'0.72rem'}}>—</span>;
    if (daysToStart < 0)  return <span style={{fontWeight:800,color:'var(--red)',fontSize:'0.75rem'}}>Started<br/><span style={{fontSize:'0.65rem'}}>{Math.abs(daysToStart)}d ago</span></span>;
    if (daysToStart === 0) return <span style={{fontWeight:800,color:'#e87830',fontSize:'0.78rem'}}>TODAY</span>;
    if (daysToStart === 1) return <span style={{fontWeight:800,color:'#f7c34f',fontSize:'0.78rem'}}>Tomorrow</span>;
    if (daysToStart <= 3)  return <span style={{fontWeight:800,color:'#f7c34f',fontSize:'0.82rem'}}>{daysToStart}<span style={{fontSize:'0.65rem',fontWeight:600}}> days</span></span>;
    return <span style={{fontWeight:700,color:'var(--text2)',fontSize:'0.82rem'}}>{daysToStart}<span style={{fontSize:'0.65rem',fontWeight:500,color:'var(--text3)'}}> days</span></span>;
  })();

  return (
    <>
      <tr style={{cursor:hasSubTabs?'pointer':'default',borderBottom:'1px solid var(--border)'}}
        onClick={() => hasSubTabs && setExpanded(v=>!v)}>
        <td style={{padding:'10px 12px'}}>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            {hasSubTabs && (
              <span style={{fontSize:'0.7rem',color:'var(--text3)',transition:'transform 0.15s',transform:expanded?'rotate(90deg)':'rotate(0deg)',display:'inline-block'}}>▶</span>
            )}
            <div>
              <div style={{fontWeight:700,fontSize:'0.82rem',color:'var(--text1)'}}>{task.title}</div>
              <div style={{display:'flex',gap:6,marginTop:2,flexWrap:'wrap',alignItems:'center'}}>
                {task.wo_number && <span style={{fontFamily:'Space Mono,monospace',fontSize:'0.65rem',color:'var(--accent)',fontWeight:700}}>{task.wo_number}</span>}
                {task.client && <span style={{fontSize:'0.68rem',color:'var(--text2)',fontWeight:600,padding:'1px 6px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:8}}>{task.client}</span>}
              </div>
            </div>
          </div>
        </td>
        <td style={{padding:'10px 12px'}}>
          <span style={{display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:'0.68rem',fontWeight:600,borderLeft:`3px solid ${phColor}`,background:`${phColor}12`,color:phColor}}>
            {task.phase||'—'}
          </span>
        </td>
        <td style={{padding:'10px 12px'}}>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <div style={{width:26,height:26,borderRadius:'50%',background:avatarBg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.62rem',color:'#fff',fontWeight:700,flexShrink:0}}>
              {memberInitials(task.assignee||'?')}
            </div>
            <span style={{fontSize:'0.78rem',color:'var(--text1)'}}>{task.assignee||'Unassigned'}</span>
          </div>
        </td>
        <td style={{padding:'10px 12px',fontSize:'0.78rem',color:'var(--text2)'}}>{PRIORITY_LABEL[task.priority?.toLowerCase()]||task.priority||'—'}</td>
        <td style={{padding:'10px 12px',whiteSpace:'nowrap'}}>
          <span style={{fontFamily:'Space Mono,monospace',fontSize:'0.72rem',color:winColor,fontWeight:700}}>
            {fmtDMY(task.stage_start)} → {fmtDMY(task.deadline)}
          </span>
          <div style={{fontSize:'0.62rem',color:winColor,fontWeight:600}}>{winBadge}</div>
        </td>
        <td style={{padding:'10px 12px',textAlign:'center'}}>{daysChip}</td>
        <td style={{padding:'10px 12px',fontFamily:'Space Mono,monospace',fontSize:'0.72rem',color:isOverdue?'var(--red)':'var(--text3)',fontWeight:isOverdue?700:400,whiteSpace:'nowrap'}}>
          {isOverdue ? '⚠️ ' : ''}{eventDisplay}
        </td>
        <td style={{padding:'10px 12px'}}>
          {(() => {
            const tt = getTabLabel(task);
            if (tt) return <span className={`badge ${TAB_BADGE[tt.tabId]||'badge-gray'}`} style={{fontSize:'0.68rem'}}>{tt.label}</span>;
            return <span className={`badge ${STATUS_BADGE[task.status]||'badge-gray'}`} style={{fontSize:'0.68rem'}}>{st.icon} {task.status||'—'}</span>;
          })()}
        </td>
        <td style={{padding:'10px 12px'}}>
          {(() => {
            const pct = getTabProgress(task);
            const pc  = pct>=100?'#1db87a':pct>=50?'#3b7ef0':'#f7c34f';
            return (
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <div style={{width:64,height:5,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:pc,borderRadius:3}} />
                </div>
                <span style={{fontSize:'0.68rem',fontWeight:700,color:pc}}>{pct}%</span>
              </div>
            );
          })()}
        </td>
        <td style={{padding:'10px 12px'}} onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',gap:4}}>
            <button
              onClick={() => onEdit(task)}
              className="btn btn-secondary"
              style={{padding:'3px 8px',fontSize:'0.7rem'}}>
              ✏️ Edit
            </button>
            {task.status === 'Blocked' && (
              <button
                onClick={() => onResume(task.id)}
                style={{padding:'3px 8px',fontSize:'0.7rem',fontWeight:700,background:'var(--accent)',color:'#fff',border:'none',borderRadius:5,cursor:'pointer'}}>
                ▶ Resume
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Expandable sub-tab progress row */}
      {expanded && hasSubTabs && (
        <tr style={{borderBottom:'1px solid var(--border)'}}>
          <td colSpan={10} style={{padding:'0 0 8px 0',background:'var(--bg3)'}}>
            <div style={{margin:'0 12px 4px'}}>
              <SubTabProgress task={task} phaseColor={phColor} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}



// ── Edit Task Modal ────────────────────────────────────────────────────────────
function EditTaskModal({ task, team, onClose, onSaved }) {
  const [assignee,  setAssignee]  = useState(task.assignee || '');
  const [priority,  setPriority]  = useState(task.priority || 'normal');
  const [notes,     setNotes]     = useState(task.notes || '');
  const [holdMode,  setHoldMode]  = useState(false);
  const [holdReason,setHoldReason]= useState(task.hold_reason || '');
  const [saving,    setSaving]    = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/tasks/${task.id}`, { assignee, priority, notes });
      onSaved();
    } finally { setSaving(false); }
  };

  const submitHold = async () => {
    if (!holdReason.trim()) { alert('Please enter a reason.'); return; }
    setSaving(true);
    try {
      await api.post(`/tasks/${task.id}/hold`, { reason: holdReason });
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,width:480,maxWidth:'96vw',padding:24}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
          <div>
            <div style={{fontWeight:700,fontSize:'0.95rem',color:'var(--text1)'}}>✏️ Edit Task</div>
            <div style={{fontSize:'0.72rem',color:'var(--text3)',marginTop:2}}>{task.wo_number} · {task.phase}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:18,cursor:'pointer',color:'var(--text3)'}}>✕</button>
        </div>

        <div style={{marginBottom:12}}>
          <label className="form-label">Assigned To</label>
          <select className="form-select" value={assignee} onChange={e=>setAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {team.filter(m=>m.employment!=='inactive').map(m=><option key={m.id} value={m.name}>{m.name}{m.role?` · ${m.role}`:''}</option>)}
          </select>
        </div>

        <div style={{marginBottom:12}}>
          <label className="form-label">Priority</label>
          <select className="form-select" value={priority} onChange={e=>setPriority(e.target.value)}>
            {['urgent','high','normal','low'].map(p=><option key={p} value={p}>{PRIORITY_LABEL[p]||p}</option>)}
          </select>
        </div>

        <div style={{marginBottom:16}}>
          <label className="form-label">Notes</label>
          <textarea className="form-textarea" style={{height:60}} value={notes}
            onChange={e=>setNotes(e.target.value)} placeholder="Any notes for this task…" />
        </div>

        {task.status === 'Blocked' && (
          <div style={{marginBottom:12,padding:'8px 12px',background:'#e8454512',border:'1px solid #e8454544',borderRadius:8,fontSize:'0.78rem',color:'var(--red)'}}>
            ⛔ On Hold: {task.hold_reason || '—'}
          </div>
        )}

        {holdMode ? (
          <div style={{marginBottom:16}}>
            <label className="form-label">Hold Reason <span style={{color:'var(--red)'}}>*</span></label>
            <input className="form-input" value={holdReason} onChange={e=>setHoldReason(e.target.value)}
              placeholder="Describe why this task is being put on hold…" />
          </div>
        ) : null}

        <div style={{display:'flex',gap:8,justifyContent:'space-between',flexWrap:'wrap'}}>
          <div style={{display:'flex',gap:8}}>
            {task.status !== 'Done' && task.status !== 'Locked' && !holdMode && task.status !== 'Blocked' && (
              <button onClick={()=>setHoldMode(true)}
                style={{padding:'6px 14px',fontSize:'0.78rem',fontWeight:700,background:'transparent',color:'var(--red)',border:`1px solid var(--red)`,borderRadius:6,cursor:'pointer'}}>
                ⏸ Hold
              </button>
            )}
            {holdMode && (
              <button onClick={()=>setHoldMode(false)} className="btn btn-secondary" style={{fontSize:'0.78rem'}}>
                Cancel Hold
              </button>
            )}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={onClose} className="btn btn-secondary" style={{fontSize:'0.78rem'}}>Cancel</button>
            {holdMode
              ? <button onClick={submitHold} disabled={saving} className="btn btn-primary" style={{fontSize:'0.78rem',background:'var(--red)',borderColor:'var(--red)'}}>
                  {saving ? 'Saving…' : '⏸ Confirm Hold'}
                </button>
              : <button onClick={submit} disabled={saving} className="btn btn-primary" style={{fontSize:'0.78rem'}}>
                  {saving ? 'Saving…' : '💾 Save'}
                </button>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Multi-select Status Filter ────────────────────────────────────────────────
function StatusMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    onChange(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  };

  const label = selected.length === 0 ? 'All Statuses'
    : selected.length === 1 ? selected[0]
    : `${selected.length} selected`;

  return (
    <div ref={ref} style={{position:'relative',userSelect:'none'}}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display:'flex',alignItems:'center',gap:8,
          padding:'6px 12px',minWidth:180,
          background:'var(--bg2)',border:'1px solid var(--border)',
          borderRadius:6,cursor:'pointer',fontSize:'0.82rem',
          color: selected.length > 0 ? 'var(--accent)' : 'var(--text2)',
          fontWeight: selected.length > 0 ? 700 : 400,
        }}>
        <span style={{flex:1,textAlign:'left'}}>{label}</span>
        {selected.length > 0 && (
          <span
            onClick={e => { e.stopPropagation(); onChange([]); }}
            style={{fontSize:11,color:'var(--text3)',padding:'0 2px',lineHeight:1}}
            title="Clear all">✕</span>
        )}
        <span style={{fontSize:10,color:'var(--text3)'}}>▼</span>
      </button>

      {open && (
        <div style={{
          position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:999,
          background:'var(--bg2)',border:'1px solid var(--border)',
          borderRadius:8,boxShadow:'0 6px 24px rgba(0,0,0,0.12)',
          minWidth:200,maxHeight:280,overflowY:'auto',padding:'6px 0',
        }}>
          {options.map(opt => {
            const checked = selected.includes(opt);
            return (
              <label key={opt}
                style={{display:'flex',alignItems:'center',gap:8,padding:'7px 14px',cursor:'pointer',
                  background: checked ? 'rgba(59,126,240,0.06)' : 'transparent',
                  fontSize:'0.82rem',color: checked ? 'var(--accent)' : 'var(--text1)',
                  fontWeight: checked ? 600 : 400}}
                onMouseOver={e => e.currentTarget.style.background = checked ? 'rgba(59,126,240,0.08)' : 'var(--bg3)'}
                onMouseOut={e => e.currentTarget.style.background = checked ? 'rgba(59,126,240,0.06)' : 'transparent'}>
                <input type="checkbox" checked={checked} onChange={() => toggle(opt)}
                  style={{accentColor:'var(--accent)',width:14,height:14,cursor:'pointer'}} />
                {opt}
              </label>
            );
          })}
          {selected.length > 0 && (
            <div style={{borderTop:'1px solid var(--border)',padding:'6px 14px',marginTop:4}}>
              <button onClick={() => onChange([])}
                style={{fontSize:'0.72rem',color:'var(--text3)',background:'none',border:'none',cursor:'pointer',padding:0}}>
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Work Assignments Page ────────────────────────────────────────────────
export default function WorkAssignments() {
  const { user } = useAuth();
  const [tasks,    setTasks]    = useState([]);
  const [team,     setTeam]     = useState([]);
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [woSearch, setWoSearch] = useState('');
  const [statusF,  setStatusF]  = useState([]); // array for multi-select
  const [assigneeF,setAssigneeF]= useState('');
  const [clientF,  setClientF]  = useState('');
  const [phaseF,   setPhaseF]   = useState('');
  const [sortF,    setSortF]    = useState('start'); // start | daystostart | event | deadline
  const [sortDir,  setSortDir]  = useState('asc');
  const [editTask, setEditTask] = useState(null);
  const [yearF,    setYearF]    = useState('');
  const [monthsF,  setMonthsF]  = useState(new Set());
  const [showMonthDd, setShowMonthDd] = useState(false);

  const toggleMonth = m => setMonthsF(prev => { const n = new Set(prev); n.has(m) ? n.delete(m) : n.add(m); return n; });

  useEffect(() => {
    if (!showMonthDd) return;
    const handler = e => { if (!e.target.closest('[data-month-dd]')) setShowMonthDd(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMonthDd]);

  const yearOptions = [...new Set(tasks.map(t => t.event_date?.slice(0,4)).filter(Boolean))].sort().reverse();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, tmRes, clRes] = await Promise.all([
        api.get('/tasks'),
        api.get('/team'),
        api.get('/clients'),
      ]);
      setTasks(tRes.data.data || []);
      setTeam(tmRes.data.data  || tmRes.data || []);
      setClients(clRes.data.data || clRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleResume = async id => { await api.post(`/tasks/${id}/resume`); load(); };
  const handleHold   = async (id, reason) => { if (!reason) return; await api.post(`/tasks/${id}/hold`, {reason}); load(); };
  const handleEdit    = (task) => setEditTask(task);

  // Filter (matches blueprint renderAssignments exactly)
  const filtered = tasks.filter(t => {
    if (t.is_ad_hoc) return false;           // ad-hoc shown separately
    if (t.show_in_assignments === false) return false; // master data flag
    if (t.status === 'Locked' && statusF !== 'Locked') return false;
    if (t.status === 'Review' && !statusF) return false; // Review goes to Approvals page
    if (statusF.length > 0) {
      const tt = getTabLabel(t);
      const label = tt ? tt.label : t.status;
      if (!statusF.includes(label)) return false;
    }
    if (assigneeF && t.assignee  !== assigneeF) return false;
    if (phaseF    && t.phase     !== phaseF)    return false;
    if (clientF   && String(t.client_id) !== clientF && t.client !== clients.find(c=>c.id.toString()===clientF)?.name) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!t.title?.toLowerCase().includes(q) && !t.client?.toLowerCase().includes(q)) return false;
    }
    if (woSearch) {
      const q = woSearch.toLowerCase();
      if (!t.wo_number?.toLowerCase().includes(q) && !t.wo_title?.toLowerCase().includes(q)) return false;
    }
    if (yearF && t.event_date?.slice(0,4) !== yearF) return false;
    if (monthsF.size > 0) {
      const m = t.event_date ? parseInt(t.event_date.slice(5,7),10)-1 : -1;
      if (!monthsF.has(m)) return false;
    }
    return true;
  }).sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortF === 'start')       { const sa = a.stage_start||a.deadline||''; const sb = b.stage_start||b.deadline||''; return sa>sb?dir:sa<sb?-dir:0; }
    if (sortF === 'daystostart') {
      const today = new Date(); today.setHours(0,0,0,0);
      const dA = a.stage_start ? Math.round((new Date(a.stage_start+'T00:00:00')-today)/86400000) : 9999;
      const dB = b.stage_start ? Math.round((new Date(b.stage_start+'T00:00:00')-today)/86400000) : 9999;
      return dA>dB?dir:dA<dB?-dir:0;
    }
    if (sortF === 'deadline')    { return (a.deadline||'')>(b.deadline||'')?dir:(a.deadline||'')<(b.deadline||'')?-dir:0; }
    if (sortF === 'event')       { return (a.event_date||'')>(b.event_date||'')?dir:(a.event_date||'')<(b.event_date||'')?-dir:0; }
    return 0;
  });

  const sortIcon = (f) => sortF===f ? (sortDir==='asc'?'↑':'↓') : '↕';
  const toggleSort = (f) => { if (sortF===f) setSortDir(d=>d==='asc'?'desc':'asc'); else { setSortF(f); setSortDir('asc'); } };

  // Badge count: Todo tasks that are visible
  const todoCount = tasks.filter(t => t.status==='Todo' && !t.is_ad_hoc && t.show_in_assignments!==false).length;
  const totalCount = tasks.filter(t => !t.is_ad_hoc && t.show_in_assignments!==false).length;
  const countLabel = filtered.length===totalCount ? `${totalCount} task${totalCount!==1?'s':''}` : `${filtered.length} of ${totalCount}`;

  return (
    <div>
      {/* Toolbar */}
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <input style={{...fltStyle,width:180}} placeholder="Search assignments…"
          value={search} onChange={e=>setSearch(e.target.value)} />
        <input style={{...fltStyle,width:200}} type="search" placeholder="🔍 WO number or title…"
          value={woSearch} onChange={e=>setWoSearch(e.target.value)} />
        <StatusMultiSelect
          options={[...new Set(
            tasks
              .filter(t => !t.is_ad_hoc && t.show_in_assignments !== false)
              .map(t => { const tt = getTabLabel(t); return tt ? tt.label : t.status; })
              .filter(Boolean)
          )].sort()}
          selected={statusF}
          onChange={setStatusF}
        />
        <select style={fltStyle} value={assigneeF} onChange={e=>setAssigneeF(e.target.value)}>
          <option value="">All Members</option>
          {team.filter(m=>m.employment!=='inactive').map(m=><option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
        <select style={fltStyle} value={phaseF} onChange={e=>setPhaseF(e.target.value)}>
          <option value="">All Phases</option>
          {[...new Set(tasks.filter(t=>!t.is_ad_hoc && t.show_in_assignments!==false).map(t=>t.phase).filter(Boolean))].sort().map(p=><option key={p}>{p}</option>)}
        </select>
        <select style={fltStyle} value={clientF} onChange={e=>setClientF(e.target.value)}>
          <option value="">All Clients</option>
          {clients.filter(c=>c.enabled!==false).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={fltStyle} value={yearF} onChange={e=>setYearF(e.target.value)}>
          <option value="">All Years</option>
          {yearOptions.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <div data-month-dd style={{position:'relative'}}>
          <button onClick={()=>setShowMonthDd(v=>!v)} style={{...fltStyle,display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
            {monthsF.size===0?'All Months':monthsF.size===1?MONTH_NAMES[[...monthsF][0]]:`${monthsF.size} months`}
            {monthsF.size>0&&<span onClick={e=>{e.stopPropagation();setMonthsF(new Set());}} style={{fontSize:10,color:'var(--text3)',lineHeight:1}}>✕</span>}
            <span style={{fontSize:10,color:'var(--text3)'}}>▼</span>
          </button>
          {showMonthDd&&(
            <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:999,background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:8,boxShadow:'0 6px 24px rgba(0,0,0,0.12)',padding:8,display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:4,minWidth:180}}>
              {MONTH_NAMES.map((mn,i)=>(
                <button key={i} onClick={()=>toggleMonth(i)}
                  style={{padding:'4px 6px',fontSize:'0.72rem',borderRadius:5,border:'1px solid var(--border)',cursor:'pointer',fontWeight:monthsF.has(i)?700:400,background:monthsF.has(i)?'var(--accent)':'var(--bg3)',color:monthsF.has(i)?'#fff':'var(--text2)'}}>
                  {mn}
                </button>
              ))}
            </div>
          )}
        </div>
        <span style={{fontSize:'0.75rem',color:filtered.length<totalCount?'var(--accent)':'var(--text2)',fontFamily:'Space Mono,monospace',marginLeft:'auto'}}>
          {countLabel}
        </span>
      </div>

      {loading ? (
        <div style={{textAlign:'center',padding:48,color:'var(--text3)'}}>Loading assignments…</div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:'center',padding:'40px',color:'var(--text3)'}}>
          {tasks.length === 0
            ? 'No tasks yet — Content Briefs client approval auto-generates tasks per workflow phase.'
            : 'No tasks match your filters.'}
        </div>
      ) : (
        <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',overflow:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:900}}>
            <thead>
              <tr style={{background:'var(--bg3)'}}>
                {[
                  { label:'Task',        sort:null },
                  { label:'Phase',       sort:null },
                  { label:'Assigned To', sort:null },
                  { label:'Priority',    sort:null },
                  { label:'Stage Window',sort:'start',       title:'Sort by stage start date' },
                  { label:'Days to Start',sort:'daystostart',title:'Sort by days until stage begins' },
                  { label:'Event Date',  sort:'event',       title:'Sort by event date' },
                  { label:'Status',      sort:null },
                  { label:'% Done',      sort:null },
                  { label:'Actions',     sort:null },
                ].map(({label,sort,title}) => (
                  <th key={label}
                    title={title}
                    onClick={sort ? ()=>toggleSort(sort) : undefined}
                    style={{padding:'10px 12px',textAlign:'left',fontSize:'0.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text3)',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap',cursor:sort?'pointer':'default',userSelect:'none'}}>
                    {label} {sort && <span style={{opacity:0.6}}>{sortIcon(sort)}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(task => (
                <TaskRow key={task.id} task={task}
                  onEdit={setEditTask}
                  onHold={handleHold} onResume={handleResume} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editTask && (
        <EditTaskModal task={editTask} team={team} onClose={() => setEditTask(null)} onSaved={() => { setEditTask(null); load(); }} />
      )}
    </div>
  );
}